import {z} from "zod";
import {effectiveStartDate, todayDateInput} from "./dates";
import {promoWindowDays, termConflict} from "./helpers";
import {
  ALLOWED_PLAN_TYPES_BY_TERM_TYPE,
  ALLOWED_UNITS_BY_TERM_TYPE,
  CADENCE_UNITS,
  MAX_PERCENT,
  PROMO_KEY_MAX_LENGTH,
  PROMO_KEY_PATTERN,
  PROMO_PLAN_TYPES,
  PROMO_STATUSES,
  PROMO_TERM_TYPES,
  PROMO_TERM_UNITS,
  PROMO_WEEKDAYS,
  PROMO_WINDOWS,
  START_MODES,
  UNITS_WITHOUT_AMOUNT,
} from "./constants";

const REQUIRED = "Required";

export const promoTermSchema = z.object({
  type: z.enum(PROMO_TERM_TYPES),
  amount: z.number().positive("Amount must be greater than 0").nullable(),
  unit: z.enum(PROMO_TERM_UNITS),
  plan_types: z.array(z.enum(PROMO_PLAN_TYPES)).min(1, "Pick at least one plan type"),
});

export const promoFormSchema = z
  .object({
    name: z.string().trim().min(1, REQUIRED),
    key: z
      .string()
      .min(1, REQUIRED)
      .max(PROMO_KEY_MAX_LENGTH, `Key can be up to ${PROMO_KEY_MAX_LENGTH} characters`)
      .regex(PROMO_KEY_PATTERN, "Key can use lowercase letters, digits and single underscores"),
    description: z.string(),
    status: z.enum(PROMO_STATUSES),
    start_mode: z.enum(START_MODES),
    starts_at: z.string(),
    start_weekday: z.enum(PROMO_WEEKDAYS),
    repeats: z.boolean(),
    ends_at: z.string(),
    window: z.enum(PROMO_WINDOWS),
    cadence_every: z.number().int("Whole numbers only").min(1, "Must be at least 1").nullable(),
    cadence_unit: z.enum(CADENCE_UNITS),
    terms: z.array(promoTermSchema).min(1, "Add at least one term"),
    segment_ids: z.array(z.number()).min(1, "Pick at least one segment"),
    templates: z.array(z.object({template_id: z.number(), days_since_start: z.array(z.number().int().min(0))})),
    no_send_days: z.array(z.number().int().min(0)),
    force_days: z.array(z.number().int().min(0)),
    reminders: z.array(
      z.object({
        day: z.number().int().min(0),
        delay_hours: z.number().int("Whole hours only").min(1, "At least 1 hour").nullable(),
      })
    ),
  })
  .superRefine((values, ctx) => {
    const windowDays = promoWindowDays(values);
    values.templates.forEach((template, index) => {
      if (windowDays !== null && template.days_since_start.some((day) => day >= windowDays)) {
        ctx.addIssue({
          code: "custom",
          path: ["templates", index, "days_since_start"],
          message: `Forced days must be within the promo window (day 0 to ${windowDays - 1})`,
        });
      }
    });
    const plannedDays = new Set([...values.force_days, ...values.no_send_days]);
    const hasModelDays = windowDays === null || plannedDays.size < windowDays;
    const needsModelTemplate = values.status !== "draft" && hasModelDays;
    const hasModelTemplate = values.templates.some((link) => link.days_since_start.length === 0);
    const emptyForceDay = values.force_days.some(
      (day) => !values.templates.some((link) => link.days_since_start.includes(day))
    );
    if (emptyForceDay) {
      ctx.addIssue({code: "custom", path: ["force_days"], message: "Pick the templates for each forced day"});
    }
    if (needsModelTemplate && !hasModelTemplate) {
      ctx.addIssue({
        code: "custom",
        path: ["templates"],
        message: "Add a template without forced days for the model to choose from",
      });
    }
    if (windowDays !== null && values.no_send_days.some((day) => day >= windowDays)) {
      ctx.addIssue({
        code: "custom",
        path: ["no_send_days"],
        message: `Send-nothing days must be within the promo window (day 0 to ${windowDays - 1})`,
      });
    }
    values.reminders.forEach((reminder, index) => {
      if (reminder.delay_hours === null) {
        ctx.addIssue({code: "custom", path: ["reminders", index, "delay_hours"], message: "Enter the hours"});
      }
    });
    if (values.start_mode === "weekday" && values.repeats && values.cadence_every === null) {
      ctx.addIssue({code: "custom", path: ["cadence_every"], message: "Enter how often the promo repeats"});
    }
    if (values.start_mode === "date" && !values.starts_at) {
      ctx.addIssue({code: "custom", path: ["starts_at"], message: "Pick a start date"});
    }
    if (values.window === "custom" && !values.ends_at) {
      ctx.addIssue({code: "custom", path: ["ends_at"], message: "A custom window needs an end date"});
    }
    const startDate = effectiveStartDate(values, todayDateInput());
    if (values.window === "custom" && values.ends_at && startDate && values.ends_at < startDate) {
      ctx.addIssue({code: "custom", path: ["ends_at"], message: "Can't be before the start date"});
    }
    values.terms.forEach((term, index) => {
      const conflict = termConflict(term, values.terms.slice(0, index));
      if (conflict) {
        ctx.addIssue({code: "custom", path: ["terms", index, "plan_types"], message: conflict});
      }
      if (!ALLOWED_UNITS_BY_TERM_TYPE[term.type].includes(term.unit)) {
        ctx.addIssue({code: "custom", path: ["terms", index, "unit"], message: "Pick a valid unit for this term"});
      }
      if (term.plan_types.some((planType) => !ALLOWED_PLAN_TYPES_BY_TERM_TYPE[term.type].includes(planType))) {
        ctx.addIssue({
          code: "custom",
          path: ["terms", index, "plan_types"],
          message: "This term can't apply to those plans",
        });
      }
      if (term.unit === "percent" && term.amount !== null && term.amount > MAX_PERCENT) {
        ctx.addIssue({code: "custom", path: ["terms", index, "amount"], message: `Max ${MAX_PERCENT}%`});
      }
      if (term.unit === "installments" && term.amount !== null && !Number.isInteger(term.amount)) {
        ctx.addIssue({code: "custom", path: ["terms", index, "amount"], message: "Whole installments only"});
      }
      if (UNITS_WITHOUT_AMOUNT.has(term.unit)) return;
      if (term.amount === null) {
        ctx.addIssue({code: "custom", path: ["terms", index, "amount"], message: "Enter an amount for each term"});
      }
    });
  });

export type PromoFormValues = z.infer<typeof promoFormSchema>;
export type PromoTermFormValues = z.infer<typeof promoTermSchema>;
